import { XlsxParser, decodeRange, encodeCell, Workbook } from "../xlsx";
import { JSONSchema9 } from "../types/jsonSchema";
import pluralize from "pluralize";

type SchemaType = "string" | "number" | "boolean" | "object" | "array";
type SheetHeaders = Record<string, Record<string, string>>;
type SampleValues = Record<string, string[]>;
type SampleValuesBySheet = Record<string, Record<string, any[]>>;

export class SpreadsheetSchemaBuilder {
  schema: JSONSchema9;
  sheetHeaders: SheetHeaders = {};
  sampleValuesBySheet: SampleValuesBySheet = {};
  belongsToProperties: Record<string, Record<string, JSONSchema9>> = {};
  unsafeSheetNames: Record<string, string> = {};

  static async addFieldsFrom(file: File, existingSchema: JSONSchema9): Promise<JSONSchema9> {
    const builder = new SpreadsheetSchemaBuilder(file, existingSchema, false);
    const schemas = await builder.generateJsonSchemas();
    return schemas;
  }

  constructor(
    private readonly file: File,
    existingSchema: JSONSchema9 = {},
    private readonly inferTypes: boolean = true,
  ) {
    this.schema = {
      ...existingSchema,
      type: "object",
      properties: { ...existingSchema.properties },
      definitions: { ...existingSchema.definitions },
      title: `${file.name}`,
      description: `Generated automatically from ${file.name}`,
    };
  }

  private inferType(values: any[]): SchemaType {
    if (!this.inferTypes) return "string";

    let type: SchemaType = "string";
    for (const value of values) {
      if (value === undefined || value === null || value === "") continue;

      const val = String(value).trim().toLowerCase();
      if (val === "true" || val === "false") {
        type = type === "string" ? "boolean" : type;
      } else if (!Number.isNaN(Number(value)) && val !== "") {
        type = type === "string" ? "number" : type;
      } else {
        return "string";
      }
    }
    return type;
  }

  /**
   * Sanitizes a string key to be a valid JavaScript identifier.
   *
   * Steps:
   * 1. Returns empty string if key is undefined/null/non-string
   * 2. Replaces any non-alphanumeric chars (except _ and $) with underscore
   * 3. Adds underscore prefix if key starts with a number
   * 4. Converts camelCase to snake_case and makes lowercase
   *
   * Examples:
   * "Hello World" -> "hello_world"
   * "123abc" -> "_123abc"
   * "user@email.com" -> "user_email_com"
   * "firstName" -> "first_name"
   * "Stakeholder" -> "stakeholder"
   * undefined -> ""
   */
  public static sanitizeKey(key: string | undefined): string {
    // 1. Return empty string if key is undefined/null/non-string
    if (!key || typeof key !== "string") return "";

    // 2. Replace any non-alphanumeric chars (except _ and $) with underscore
    let clean = key.replace(/[^a-zA-Z0-9_$]/g, "_");

    // 3. Add underscore prefix if key starts with a number
    if (/^\d/.test(clean)) clean = "_" + clean;

    // 4. Convert camelCase to snake_case and make lowercase
    clean = clean
      .replace(/([a-z0-9])([A-Z])/g, "$1_$2") // insert _ before capitals
      .replace(/([A-Z])([A-Z][a-z])/g, "$1_$2") // handle ALLCAPS followed by CapitalLower
      .toLowerCase();

    return clean;
  }

  /**
   * Instance wrapper for static sanitizeKey for conciseness.
   */
  private sanitize(key: string | undefined): string {
    return SpreadsheetSchemaBuilder.sanitizeKey(key);
  }

  public async generateJsonSchemas(): Promise<JSONSchema9> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = async (e: ProgressEvent<FileReader>) => {
        try {
          if (!e.target?.result) {
            throw new Error("Failed to read file");
          }

          const data = new Uint8Array(e.target.result as ArrayBuffer);
          const workbook = await XlsxParser.parse(data);
          this.buildSchema(workbook);
          resolve(this.schema);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsArrayBuffer(this.file);
    });
  }

  private buildSchema(workbook: Workbook) {
    this.processWorkbook(workbook);
    this.buildSheetSchemas(this.sheetHeaders, this.sampleValuesBySheet);
    this.addBelongsToProperties();
  }

  private processWorkbook(workbook: Workbook): void {
    this.unsafeSheetNames = {};
    this.sheetHeaders = {};
    this.sampleValuesBySheet = {};

    for (const sheetName of workbook.SheetNames) {
      const worksheet = workbook.Sheets[sheetName];
      const ref = worksheet["!ref"];
      if (!ref) continue;

      const range = decodeRange(ref);
      const headers: Record<string, string> = {};
      const samples: SampleValues = {};

      for (let C = range.s.c; C <= range.e.c; ++C) {
        const headerCell = worksheet[encodeCell({ r: range.s.r, c: C })];
        const header =
          headerCell && typeof headerCell === "object" && "v" in headerCell ? headerCell.v : `UNKNOWN_${C}`;
        headers[this.sanitize(String(header))] = String(header);
        samples[String(header)] = [];

        for (let R = range.s.r + 1; R <= Math.min(range.e.r, range.s.r + 6); ++R) {
          const cell = worksheet[encodeCell({ r: R, c: C })];
          if (cell && typeof cell === "object" && "v" in cell && cell.v !== undefined) {
            samples[String(header)].push(cell.v as string);
          }
        }
      }

      const sanitizedSheetName = pluralize.plural(this.sanitize(sheetName));
      // track the original sheet name for display purposes
      this.unsafeSheetNames[sanitizedSheetName] = sheetName;
      this.sheetHeaders[sanitizedSheetName] = headers;
      this.sampleValuesBySheet[sanitizedSheetName] = samples;
    }
  }

  private buildSheetSchemas(sheetHeaders: SheetHeaders, sampleValuesBySheet: SampleValuesBySheet): void {
    let hasSpreadsheetFields = false;
    for (const [sheetName, headers] of Object.entries(sheetHeaders)) {
      this.buildSheetSchema(sheetName, headers, sampleValuesBySheet[sheetName]);

      const unsafeSheetName = this.unsafeSheetNames[sheetName];
      const pluralSheetName = pluralize.plural(unsafeSheetName);
      this.schema.properties![sheetName] = {
        type: "array",
        title: pluralSheetName,
        description: `A collection of ${pluralSheetName}`,
        items: {
          $ref: this.getRefForSheetName(sheetName),
        },
      };

      const singularSheetName = pluralize.singular(sheetName);
      this.schema.properties![singularSheetName] = {
        title: pluralize.singular(unsafeSheetName),
        $ref: this.getRefForSheetName(sheetName),
      };

      hasSpreadsheetFields = true;
    }
    if (hasSpreadsheetFields) {
      // Place spreadsheetUpload at the root, at the same level as x-render-condition
      (this.schema as any).spreadsheetUpload = true;
    }
  }

  private isForeignKeyHeader(header: string): boolean {
    if (!header.includes(".")) return false;
    const [first, second] = header.split(".");
    const safeFirst = pluralize.plural(this.sanitize(first));
    const safeSecond = this.sanitize(second);

    return Object.keys(this.sheetHeaders[safeFirst] || {}).includes(safeSecond);
  }

  private isInlineObjectHeader(header: string): boolean {
    return header.includes(".");
  }

  private deriveDefinitionNameFromHeader(header: string): string {
    const [first] = header.split(".");

    const safeKey = this.sanitize(first);
    if (safeKey.startsWith("generated_")) return safeKey;
    return `generated_${safeKey}`;
  }

  /**
   * Converts a spreadsheet header into a JSON Schema definition reference.
   *
   * Example:
   * Input: "Name" -> Output: "#/definitions/generated_name"
   *
   * @param header - The spreadsheet column header to convert
   * @returns The JSON Schema reference path to the definition
   */
  private getRefForHeader(header: string): string {
    return `#/definitions/${this.deriveDefinitionNameFromHeader(header)}`;
  }

  /**
   * Converts a sheet name into a JSON Schema definition reference.
   *
   * Steps:
   * 1. Takes a sheet name as input
   * 2. Converts the sheet name to its singular form
   * 3. Uses getRefForHeader to generate the reference path
   * 4. Returns the full JSON Schema reference path
   *
   * Example:
   * Input: "Stakeholders" -> Output: "#/definitions/generated_stakeholder"
   *
   * @param sheetName - The sheet name to convert
   * @returns The JSON Schema reference path to the definition
   */
  private getRefForSheetName(sheetName: string): string {
    return this.getRefForHeader(pluralize.singular(sheetName));
  }

  private buildSheetSchema(
    sheetName: string,
    headers: Record<string, string>,
    sampleValues: SampleValues,
  ): JSONSchema9 {
    const sheetSchema = this.getOrInitSheetSchema(sheetName);

    for (const [safeKey, header] of Object.entries(headers)) {
      const sampleVals = sampleValues[header] || [];
      const originalKey = header;

      if (this.isForeignKeyHeader(header)) {
        const [unsafeForeignSheetName, unsafeForeignKey] = header.split(".");

        const foreignSheetName = pluralize.plural(this.sanitize(unsafeForeignSheetName));
        const foreignSheetNameSingular = pluralize.singular(foreignSheetName);

        const unsafeSheetName = this.unsafeSheetNames[sheetName];
        const unsanitizedForeignSheetName = this.unsafeSheetNames[foreignSheetName];
        const unsanitizedForeignSheetNameSingular = pluralize.singular(unsanitizedForeignSheetName);

        // Add pointer to the referenced object
        this.belongsToProperties[sheetName] ||= {};
        this.belongsToProperties[sheetName][foreignSheetNameSingular] = {
          title: unsanitizedForeignSheetNameSingular,
          description: `Reference to ${unsanitizedForeignSheetName} by ${unsafeForeignKey}`,
          $ref: this.getRefForSheetName(foreignSheetName),
        };

        this.getOrInitSheetSchema(foreignSheetName).properties![sheetName] = {
          type: "array",
          title: unsafeSheetName,
          description: `Reference to ${unsafeSheetName} by ${unsanitizedForeignSheetNameSingular}`,
          items: {
            $ref: this.getRefForSheetName(sheetName),
          },
        };
      } else if (this.isInlineObjectHeader(header)) {
        const [first, second] = header.split(".");
        const safeFirst = this.sanitize(first);
        const safeSecond = this.sanitize(second);

        sheetSchema.properties![safeFirst] ||= { type: "object", properties: {} } as JSONSchema9;
        const inferred = this.inferType(sampleVals);
        (sheetSchema.properties![safeFirst] as JSONSchema9).properties![safeSecond] = {
          type: inferred,
          title: second,
        };
      } else {
        sheetSchema.properties![safeKey] = {
          type: this.inferType(sampleVals),
          title: originalKey,
        };
      }
    }

    this.addSignTab(sheetSchema);

    return sheetSchema;
  }

  /**
   * When the sheet has email and name columns, add a sign_tab property.
   */
  private addSignTab(sheetSchema: JSONSchema9) {
    if (!sheetSchema.properties) return;

    if ("email" in sheetSchema.properties) {
      sheetSchema.properties.sign_tab = {
        $ref: "#/definitions/sign_tab",
      };
    }
  }

  /**
   * This adds a property for sheets that point to other sheets.
   *
   * For example:
   * 1. The shares sheet points to a stakeholder on the stakeholders sheet.
   * 2. On the initial pass:
   *   i. We add a `Shares` definition that doesn't include a stakeholder property to avoid circular references.
   *   ii. We add a `Shares` property to the `Stakeholders` definition that points to the `Shares` definition.
   * 3. On the second pass:
   *   i. We copy the `Stakeholders` definition, delete the `Shares` property, and rename it to `Stakeholder_without_Shares`.
   *   ii. We add a `Stakeholder` property to the `Shares` definition that points to the `Stakeholder_without_Shares` definition.
   */
  private addBelongsToProperties() {
    Object.entries(this.belongsToProperties).forEach(([sheetName, properties]) => {
      Object.entries(properties).forEach(([propertyName, propertySchema]) => {
        if (!propertySchema.$ref) return;

        const { newDefinitionName, newDefinition } = this.cloneDefinitionWithoutProperty({
          ref: propertySchema.$ref,
          omit: sheetName,
        });

        // Add the new foreign object definition
        this.setDefinition(newDefinitionName, newDefinition);

        // Add the new property to the original definition
        const originalDefinition = this.getDefinitionForSheetName(sheetName);

        originalDefinition.properties![propertyName] = {
          title: propertySchema.title,
          $ref: this.getRefForHeader(newDefinitionName),
        };
      });
    });
  }

  /**
   * Gets the JSON schema definition for a given sheet name.
   *
   * A sheet is a collection of rows and a definition is the schema for a single row.
   * The name of the definition is derived from the singular name of the sheet.
   *
   * This method:
   * 1. Converts the plural sheet name to singular (e.g. "Stakeholders" -> "Stakeholder")
   * 2. Derives the definition name from the singular sheet name
   * 3. Returns the corresponding JSON schema definition
   *
   * @param sheetName - The plural name of the sheet (e.g. "Stakeholders")
   * @returns The JSON schema definition for the sheet
   * @throws If the definition cannot be found
   */
  private getDefinitionForSheetName(sheetName: string): JSONSchema9 {
    const singularSheetName = pluralize.singular(sheetName);
    const definitionName = this.deriveDefinitionNameFromHeader(singularSheetName);
    return this.getDefinition(definitionName) as JSONSchema9;
  }

  private cloneDefinitionWithoutProperty({ ref, omit }: { ref: string; omit: string }) {
    const definitionName = ref.replace("#/definitions/", "");
    const definition = this.getDefinition(definitionName) as JSONSchema9;
    if (!definition || typeof definition !== "object") throw new Error(`Definition ${ref} not found`);

    const newDefinition = JSON.parse(JSON.stringify(definition)) as JSONSchema9;
    delete newDefinition.properties?.[omit];

    return { newDefinitionName: `${definitionName}_without_${omit}`, newDefinition };
  }

  /**
   * Helper to get a definition by name.
   */
  private getDefinition(name: string): JSONSchema9 | undefined {
    return this.schema.definitions?.[name] as JSONSchema9;
  }

  /**
   * Helper to set a definition by name.
   */
  private setDefinition(name: string, def: JSONSchema9): void {
    if (!this.schema.definitions) this.schema.definitions = {};
    this.schema.definitions[name] = def;
  }

  /**
   * Returns the schema for a sheet, creating it if it doesn't exist.
   *
   * This creates a definition with a singular naming (e.g., "Shareholders" -> "Shareholder")
   *
   * @param sheetName The name of the sheet to get the schema for (expected to be plural)
   * @returns The schema for the sheet.
   */
  private getOrInitSheetSchema(sheetName: string): JSONSchema9 {
    const definition = this.getDefinitionForSheetName(sheetName);

    if (definition) {
      return definition;
    } else {
      const unsafeSheetName = this.unsafeSheetNames[sheetName];
      const singularSheetName = pluralize.singular(unsafeSheetName);
      const definitionName = this.deriveDefinitionNameFromHeader(singularSheetName);

      this.setDefinition(definitionName, {
        title: singularSheetName,
        description: `A row from the ${unsafeSheetName} sheet`,
        type: "object",
        properties: {},
      } as JSONSchema9);

      return this.getDefinition(definitionName) as JSONSchema9;
    }
  }
}
